<template>
  <IndicatorCard
    :loading="loading"
    :has-data="costs.length > 0"
    :is-result="isResult"
    :no-data-message="$t('Nenhum dado encontrado')"
    :title="title"
    :description="indicator.description"
    :excel-endpoint="indicator.excelEndpoint"
    :hide-excel="!isResult"
    :excel-loading="excelLoading"
    @export-excel="requestExcel"
  >
    <template #date-range>
      <DateRangePicker
        :start-date="startDate"
        :end-date="endDate"
        :disabled="isResult"
        @change="onDateRangeChange"
      />
    </template>

      <v-row class="ma-0" justify="center">
        <v-col cols="12">
          <strong>
            Total:
            {{ total ? formatCurrency(total) : '$0,00' }}
          </strong>

          <div class="opex-summary" v-if="totalPlannedOpex">
            <span>
              <strong>{{ $t('OPEX Planejado') }}:</strong>
              {{ formatCurrency(totalPlannedOpex) }}
            </span>
            <span>
              <strong>{{ $t('Saldo Disponível') }}:</strong>
              <span :class="totalOpexBalance < 0 ? 'opex-over-budget' : ''">
                {{ formatCurrency(totalOpexBalance) }}
              </span>
            </span>
            <span v-if="totalOpexUsedPercentage !== null">
              <strong>{{ $t('% do Orçamento Utilizado') }}:</strong>
              <span
                :class="totalOpexUsedPercentage > 100 ? 'opex-over-budget' : ''"
              >
                {{ totalOpexUsedPercentage.toFixed(2) }}%
              </span>
            </span>
          </div>

          <div>
            <v-data-table
              :loading="loading"
              :headers="header"
              :items="costs"
              class="main-table"
              :hide-default-footer="!filterProp.data"
              hide-default-header
              :header-props="headerProps"
              :items-per-page="7"
              :mobile-breakpoint="0"
              :expanded.sync="expanded"
              item-key="departmentName"
              show-expand
            >
              <template #header="{ props, on }">
                <thead>
                  <tr>
                    <th
                      class="text-center"
                      v-for="header in props.headers"
                      :key="header.value"
                    >
                      <v-simple-checkbox
                        v-if="header.value === 'data-table-select'"
                        v-model="props.everyItem"
                        :indeterminate="props.someItems && !props.everyItem"
                        color="purple"
                        @input="on['toggle-select-all']"
                      ></v-simple-checkbox>

                      <div>
                        <v-hover v-slot="{ hover }">
                          <div>
                            <span>{{ $t(header.text) }}</span>
                            <v-icon
                              v-if="
                                hover &&
                                header.value != 'data-table-select' &&
                                header.value != 'actions'
                              "
                              color="white"
                              class="mx-2"
                              @click="on.sort(header.value)"
                            >
                              {{
                                props.options.sortBy.includes(header.value)
                                  ? `mdi-chevron-down`
                                  : `mdi-chevron-up`
                              }}
                            </v-icon>
                          </div>
                        </v-hover>
                      </div>
                    </th>
                  </tr>
                </thead>
              </template>

              <template v-slot:[`item.department`]="{ item }">
                {{ item.departmentName }}
              </template>

              <template v-slot:[`item.totalPieceCosts`]="{ item }">
                {{ item.totalPieceCosts ? formatCurrency(item.totalPieceCosts) : '$0,00' }}
              </template>

              <template v-slot:[`item.totalServiceCosts`]="{ item }">
                {{ item.totalServiceCosts ? formatCurrency(item.totalServiceCosts) : '$0,00' }}
              </template>

              <template v-slot:[`item.totalContractCosts`]="{ item }">
                {{ item.totalContractCosts ? formatCurrency(item.totalContractCosts) : '$0,00' }}
              </template>

              <template v-slot:[`item.totalEtapaCosts`]="{ item }">
                {{ item.totalEtapaCosts ? formatCurrency(item.totalEtapaCosts) : '$0,00' }}
              </template>

              <template v-slot:[`item.totalCostsValue`]="{ item }">
                {{ item.totalCostsValue ? formatCurrency(item.totalCostsValue) : '$0,00' }}
              </template>

              <template v-slot:[`item.plannedOpex`]="{ item }">
                {{ item.plannedOpex ? formatCurrency(item.plannedOpex) : '-' }}
              </template>

              <template v-slot:[`item.opexBalance`]="{ item }">
                <span
                  v-if="item.plannedOpex"
                  :class="item.opexBalance < 0 ? 'opex-over-budget' : ''"
                >
                  {{ formatCurrency(item.opexBalance) }}
                </span>
                <span v-else>-</span>
              </template>

              <template v-slot:[`item.opexUsedPercentage`]="{ item }">
                <span
                  v-if="item.opexUsedPercentage !== null"
                  :class="item.opexUsedPercentage > 100 ? 'opex-over-budget' : ''"
                >
                  {{ item.opexUsedPercentage.toFixed(2) }}%
                </span>
                <span v-else>-</span>
              </template>

              <template #expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="opex-evolution-cell">
                  <div
                    v-if="item.monthlyEvolution && item.monthlyEvolution.length"
                    class="opex-evolution"
                  >
                    <div
                      v-for="month in item.monthlyEvolution"
                      :key="`${month.year}-${month.month}`"
                      class="opex-evolution-item"
                    >
                      <span class="opex-evolution-label">
                        {{ $t(monthNames[month.month - 1]) }}/{{ month.year }}
                      </span>
                      <span>{{ formatCurrency(month.totalCostsValue) }}</span>
                    </div>
                  </div>
                  <span v-else>
                    {{ $t('Sem gastos registrados no ano') }}
                  </span>
                </td>
              </template>
            </v-data-table>
            <div class="pagination" v-if="!filterProp.data">
              <div class="item-pagination block-two">
                <v-pagination
                  class="pagination-item"
                  v-model="page"
                  :length="totalPages"
                  :total-visible="9"
                ></v-pagination>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>

    <template #review>
    

    <v-row class="ma-0">
      <v-col cols="12" v-if="isResult && review">
        <strong>{{ $t('Análise crítica') }}</strong>
        <p v-html="review"></p>
      </v-col>
      <v-col cols="12" v-else-if="!isResult">
        <tiptap-vuetify
          v-model="filter.review"
          :extensions="extensions"
          :label="$t('Análise crítica')"
          :placeholder="
            $t('Digite sua análise crítica para o indicador abaixo')
          "
          :card-props="{ flat: true, outlined: true, rounded: true }"
          class="primary-input"
          outlined
          dense
          hide-details="auto"
        ></tiptap-vuetify>
      </v-col>
    </v-row>
    </template>
  </IndicatorCard>
</template>

<script>
import { getIndicatorById } from '../../../../../indicators/indicatorRegistry'
import ApiService from '../../../../../../../../services/ApiService'
import Utils from '../../../../../../../../Utils/FormatDate'
import moment from 'moment'
import IndicatorCard from '../../shared/IndicatorCard.vue'
import DateRangePicker from '../../shared/DateRangePicker.vue'
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
} from 'tiptap-vuetify'

export default {
  props: {
    title: { type: String, default: '' },
    apiEndpointOverride: { type: String, default: '' },
    snapshotData: { type: Object, default: null },
    filterProp: Object,
    institution: Object,
    unidades: Array,
    isResult: { type: Boolean, default: false },
    review: String,
  },

  components: {
    IndicatorCard,
    DateRangePicker,
    TiptapVuetify,
  },

  data() {
    return {
      excelLoading: false,
      extensions: [
        History,
        Blockquote,
        Link,
        Underline,
        Strike,
        Italic,
        ListItem,
        BulletList,
        OrderedList,
        [
          Heading,
          {
            options: {
              levels: [1, 2, 3],
            },
          },
        ],
        Bold,
        Code,
        HorizontalRule,
        Paragraph,
        HardBreak,
      ],
      filter: {
        page: 1,
        pageSize: 7,
        institutionId: null,
        unidadeIds: [],
        startDate: null,
        endDate: null,
        review: '',
      },
      apiService: new ApiService(),
      loading: true,
      page: 1,
      showChart: true,
      total: 0,
      totalPages: 1,
      pageSize: 7,
      costs: [],
      headerProps: {
        sortByText: 'Ordenar por',
      },
      header: [
        {
          text: 'Setor',
          align: 'center',
          value: 'department',
          width: '180px',
        },
        {
          text: 'Custo por Peça',
          align: 'center',
          value: 'totalPieceCosts',
        },
        {
          text: 'Custo por Serviço Externo',
          align: 'center',
          value: 'totalServiceCosts',
        },
        {
          text: 'Custo Logístico',
          align: 'center',
          value: 'totalLogisticCosts',
        },
        {
          text: 'Custo por Contrato',
          align: 'center',
          value: 'totalContractCosts',
        },
        {
          text: 'Custo Mão-de-Obra Interna',
          align: 'center',
          value: 'totalEtapaCosts',
        },
        {
          text: 'Custo Total',
          align: 'center',
          value: 'totalCostsValue',
        },
        {
          text: 'OPEX Planejado',
          align: 'center',
          value: 'plannedOpex',
        },
        {
          text: 'Saldo Disponível',
          align: 'center',
          value: 'opexBalance',
        },
        {
          text: '% do Orçamento Utilizado',
          align: 'center',
          value: 'opexUsedPercentage',
        },
      ],
      expanded: [],
      monthNames: [
        'Jan',
        'Fev',
        'Mar',
        'Abr',
        'Mai',
        'Jun',
        'Jul',
        'Ago',
        'Set',
        'Out',
        'Nov',
        'Dez',
      ],
      totalPlannedOpex: 0,
      totalOpexBalance: 0,
      totalOpexUsedPercentage: null,
      startDate: '',
      endDate: '',
    }
  },

  async created() {
    this.filter.institutionId = this.institution.id
    this.filter.unidadeIds = this.unidades

    this.startDate = moment()
      .add(-1, 'month')
      .clone()
      .startOf('month')
      .format('YYYY-MM-DD')

    this.endDate = moment()
      .add(-1, 'month')
      .clone()
      .endOf('month')
      .format('YYYY-MM-DD')

    if (this.filterProp) {
      this.filter = this.filterProp
      this.startDate = moment(this.filter.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')

      this.endDate = moment(this.filter.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    }

    this.list()
  },
  computed: {
    indicator() {
      return getIndicatorById('cost_per_sector') || {}
    },
  },


  methods: {
    onDateRangeChange({ startDate, endDate }) {
      this.startDate = startDate
      this.endDate = endDate
      this.filter.page = 1
      this.page = 1
      this.filterProp.data = null
      this.list()
    },

    requestExcel() {
      if (!this.indicator.excelEndpoint) return
      this.excelLoading = true
      const filename = `${this.indicator.legacyKey}.xlsx`
      this.apiService
        .post(this.indicator.excelEndpoint, { ...this.filter, pageSize: 999999 })
        .then((response) => {
          const data = `data:application/octet-stream;base64,${response.content}`
          const link = document.createElement('a')
          link.href = data
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
        .finally(() => {
          this.excelLoading = false
        })
    },

    setDates() {
      this.filter.startDate = moment(this.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')

      this.filter.endDate = moment(this.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    },
    setInfos() {
      const model = {
        chart: getIndicatorById('cost_per_sector').legacyKey,
        ...this.filter,
        data: null,
      }
      this.$emit('setInfos', model)
    },

    formatCurrency(value) {
      if (!value) return '$0,00'
      const stringValue = value.toFixed(2)
      const parts = stringValue.split('.')
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      return `$${parts.join(',')}`
    },

    formatDate(date) {
      return Utils.dateTime(date)
    },

    async list() {
      this.loading = true
      try {
        if (this.filterProp.data) {
        const response = JSON.parse(this.filterProp.data)
        this.setResponse(response)
        return
        }

        if (!this.isResult) this.setDates()
        this.filter.institutionId = this.institution.id
        this.filter.pageSize = 7
        this.filter.unidadeIds = this.unidades
        const response = await this.apiService.post(
        this.apiEndpointOverride || 'reports/listCostsDepartment', this.filter
        )
        this.setResponse(response)
      } finally {
        this.loading = false
      }
    },

    setResponse(response) {
      this.costs = []
      this.total = response.content.totalCosts
      this.showChart = response.content.objects.length > 0
      this.costs = response.content.objects
      this.totalPages = response.content.pager.totalPages
      this.expanded = []
      this.totalPlannedOpex = response.content.totalPlannedOpex
      this.totalOpexBalance = response.content.totalOpexBalance
      this.totalOpexUsedPercentage = response.content.totalOpexUsedPercentage
    },
  },

  watch: {
    page() {
      this.filterProp.page = this.page
      this.list()
    },

    filter: {
      deep: true,
      handler() {
        this.setInfos()
      },
    },
  },
}
</script>

<style scoped>
.opex-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 4px;
}

.opex-over-budget {
  color: #d32f2f;
  font-weight: 600;
}

.opex-evolution-cell {
  padding: 12px 16px;
}

.opex-evolution {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.opex-evolution-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 90px;
}

.opex-evolution-label {
  font-weight: 600;
}
</style>
